import React from "react";
import { useQuery, useInfiniteQuery, useQueryClient } from "react-query";
import {
  addNewDividend,
  fetchDividend,
  fetchTotalDividendSum,
  fetchAllCompany,
  fetchAllCrypto,
  deleteDividend,
  updateDividend,
  fetchAllAssets,
  fetchPortfolioCharts,
} from "../api";
import Spinner from "../components/Spinner/Spinner";
import { useNotifications } from "../helpers/useNotifications";
import Table from "../components/Table/Table";
import moment from "moment/moment";
import formatNumber from "../helpers/formatNumber";
import Grid from "../components/Grid/Grid";
import LoadingButton from "../components/LoadingButton/LoadingButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSquarePlus,
  faHandHoldingDollar,
  faMoneyBillTrendUp,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../components/Modal/Modal";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import Charts from "../components/Charts/Charts";
import Image from "../components/Image/Image";
import { GlobalContext } from "../context/GlobalContext";

function Dividend({ title }) {
  const { isMobile } = React.useContext(GlobalContext);
  const createNotification = useNotifications();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth;
  const limit = 25;
  const [showDividendModal, setShowDividendModal] = React.useState(false);
  const [showDividendEdit, setShowDividendEdit] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(undefined);
  const [date, setDate] = React.useState(selectedRow?.date || "");
  const [dividendValue, setDividendValue] = React.useState(
    selectedRow?.investment || ""
  );

  React.useEffect(() => {
    if (selectedRow) {
      setDate(selectedRow?.date);
      setDividendValue(selectedRow?.value);
    }
  }, [selectedRow]);

  const { data: allCompany } = useQuery(["allCompany"], () =>
    fetchAllCompany()
  );
  const { data: allCrypto } = useQuery(["allCrypto"], () => fetchAllCrypto());
  const { data: allAssets } = useQuery(["allAssets"], () => fetchAllAssets());

  //* load portfolio charts
  const { data: dbPortfolioCharts } = useQuery(
    ["myPortfolioCharts"],
    fetchPortfolioCharts
  );

  //* load total sum of dividends
  const {
    data: dbTotalDividend,
    isLoading,
    isError,
    error,
  } = useQuery(["myTotalDividends"], fetchTotalDividendSum);

  //* load dividend payments by limit
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(
      ["mydividends"],
      async ({ pageParam = 0 }) => {
        return fetchDividend(pageParam, limit);
      },
      {
        getNextPageParam: (lastPage, allPages) => {
          // Wenn weniger als limit Elemente kamen, ist Schluss
          if (lastPage.length < limit) return undefined;
          return allPages.length; // nächste page
        },
        // 👇 Deine zusätzlichen Optionen
        staleTime: 1000 * 60 * 5, // 5 Minuten Frischezeit
        cacheTime: 1000 * 60 * 10, // 10 Minuten im Cache
        refetchOnWindowFocus: false, // Kein Refetch bei Fokus
        refetchOnMount: false, // Kein Refetch beim Mount
        enabled: currentUser?.authenticated,
      }
    );

  const allItems = data?.pages.flat() || [];

  if (isLoading) return <Spinner />;
  if (isError) createNotification("error", error.message);

  const columns = [
    {
      label: "",
      field: "ticker",
      colWidth: "75%",
      rowValue: (_, row) => (
        <div className="dividend-payment-container">
          <div className="dividend-payment-image-container">
            <Link to={`/blog/${row.company.category || ""}/${row.ticker}`}>
              <img
                src={
                  row.company?.logo ||
                  `https://assets.parqet.com/logos/symbol/${row.ticker}`
                }
                alt={row.ticker}
                className="table-thumbnail"
                height={45}
                width={45}
                style={{ marginRight: 8 }}
              />
            </Link>
          </div>
          <div className="dividend-payment-text-container">
            <div className="dividend-payment-date text-muted">
              {moment(row?.date).format("DD.MM.YYYY")}&nbsp;&nbsp;
              <span className="badge text-muted bg-dividend-type">
                {row?.company?.category === "assets"
                  ? "Zinsen"
                  : row?.comapny?.category === "crypto"
                  ? "Staking"
                  : "Dividende"}
              </span>
            </div>

            <div className="dividend-payment-name text-light">
              {row.company?.name || row?.ticker}
            </div>
          </div>
        </div>
      ),
    },
    {
      label: "",
      field: "value",
      colWidth: "25%",
      rowValue: (val) => (
        <div className="d-flex align-items-center p-3 justify-content-end">
          <span className="dividend-payment-value  badge badge-success">
            + {formatNumber(val)} €
          </span>
        </div>
      ),
    },
  ];

  const chartData = dbTotalDividend?.dividendAllocation?.map((stock) => ({
    name: stock.company?.name,
    value: stock.totalDividends || 0,
    absolute: stock.totalDividends || 0,
  }));

  const divRate =
    dbTotalDividend?.totalDividendLastYear /
    dbPortfolioCharts?.keyfacts?.invested;

  return (
    <>
      {!isMobile ? (
        <Grid>
          <div className="col-6">
            <Charts.Pie
              dataset={chartData}
              colors={[
                "#2F5571",
                "#7266AE",
                "#A59CCE",
                "#D87FA0",
                "#E46682",
                "#E47A4C",
                "#F2B42B",
                "#4098A2",
                "#2E5071",
                "#7B8FA1",
              ]}
              startPosition={180}
              endPosition={0}
              innerRadius="75%"
              animated={true}
              label={`${formatNumber(dbTotalDividend?.totalDividend)} €`}
              metaLabel="Erträge"
              className="pie-chart-portfolio-container"
            />
          </div>
          <div className="col-6" style={{ height: "200px" }}>
            {dbTotalDividend?.datasets?.some((dataset) =>
              dataset?.some((value) => value && value > 0)
            ) && (
              <Charts.Line
                labels={dbTotalDividend?.labels}
                datasets={dbTotalDividend?.datasets}
                datasetLabels={dbTotalDividend?.datasetLabels}
                colors={["darkgrey", "lawngreen", "rgba(126, 252, 0, 0.6)"]}
              />
            )}
          </div>
        </Grid>
      ) : (
        <Charts.Pie
          showActiveKey
          dataset={chartData}
          colors={[
            "#1E7BC6",
            "#7266AE",
            "#A59CCE",
            "#D87FA0",
            "#E46682",
            "#E47A4C",
            "#F2B42B",
            "#4098A2",
            "#2E9EFF",
            "#7B8FA1",
          ]}
          startPosition={180}
          endPosition={0}
          innerRadius="75%"
          animated={true}
          label={`${formatNumber(dbTotalDividend?.totalDividend)} €`}
          metaLabel="Erträge"
          className="pie-chart-portfolio-container"
        />
      )}

      <br />
      <div>
        <Grid>
          <div className="col-6 col-lg-3 text-center portfolio-dashboard-grid">
            <div className="rounded portfolio-dashboard-keyfacts">
              <FontAwesomeIcon
                icon={faHandHoldingDollar}
                className="text-muted"
              />
              <p className="stockinfo-keyfacts-value text-lawngreen">
                {`${formatNumber(
                  dbTotalDividend?.totalDividendThisCurrentYear.toFixed(2)
                )}`}
              </p>
              <p className="portfolio-keyfacts-label text-muted">2026</p>
            </div>
          </div>

          <div className="col-6 col-lg-3 text-center portfolio-dashboard-grid">
            <div className="rounded portfolio-dashboard-keyfacts">
              <FontAwesomeIcon
                icon={faMoneyBillTrendUp}
                className="text-muted"
              />
              <p className="stockinfo-keyfacts-value text-lawngreen">
                {`${formatNumber(divRate * 100)} %`}
              </p>
              <p className="portfolio-keyfacts-label text-muted">
                Div. Rendite
              </p>
            </div>
          </div>

          {isMobile &&
            dbTotalDividend?.datasets?.some((dataset) =>
              dataset?.some((value) => value && value > 0)
            ) && (
              <Charts.Line
                labels={dbTotalDividend?.labels}
                datasets={dbTotalDividend?.datasets}
                datasetLabels={dbTotalDividend?.datasetLabels}
                colors={["darkgrey", "lawngreen", "rgba(126, 252, 0, 0.6)"]}
                className="portfolio-dividend-chart rounded portfolio-dashboard-keyfacts"
              />
            )}
        </Grid>
      </div>

      <div className="article-main">
        <Grid>
          <h5
            className="text-light text-center"
            style={{ margin: "15px 0px 0px 0px" }}
          >
            Auszahlungen{" "}
            <span className="dividend-payment-subtitle text-muted">
              ({dbTotalDividend?.totalPayments})
            </span>
            <button
              className="btn btn-link dismiss-active-or-focus delete-form-label"
              data-toggle="modal"
              data-target="#addDividend"
              onClick={(e) => {
                e.preventDefault();
                setShowDividendModal((prev) => !prev);
              }}
            >
              <FontAwesomeIcon icon={faSquarePlus} />
            </button>
          </h5>

          <Table
            data={allItems}
            columns={columns}
            onClick={(row) => {
              setShowDividendEdit(true);
              setSelectedRow(row);
            }}
          />
        </Grid>
      </div>
      {hasNextPage && (
        <div className="text-center mt-6">
          <LoadingButton
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
          />
        </div>
      )}

      <Modal
        id="addDividend"
        visible={showDividendModal}
        setVisible={setShowDividendModal}
        title="Auszahlung hinzufügen"
        onSave={(formData) => {
          if (formData?.ticker && formData?.date && formData?.value) {
            addNewDividend(formData?.ticker, formData?.date, formData?.value)
              .then((result) => {
                createNotification("success", result.message);
                queryClient.invalidateQueries(["myTotalDividends"]);
                queryClient.invalidateQueries(["mydividends"]);
              })
              .catch((err) => {
                createNotification("error", err.message);
              });
          } else {
            createNotification("error", "Bitte alle Daten angeben");
          }
        }}
      >
        <div className="form-group col-12">
          <label className="text-dark" for="ticker">
            Ticker
          </label>
          <div className="input-group">
            <select
              className="form-control"
              id="ticker"
              name="ticker"
              data-live-search="true"
              showContent="true"
            >
              <optgroup label="Aktien">
                {allCompany?.map((el) => (
                  <option key={el?.ticker} value={el?.ticker}>
                    {el?.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Krypto">
                {allCrypto?.map((el) => (
                  <option key={el?.ticker} value={el?.ticker}>
                    {el?.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Assets">
                {allAssets?.map((el) => (
                  <option key={el?.ticker} value={el?.ticker}>
                    {el?.name}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
        </div>

        <div className="form-group col-12">
          <label className="text-dark" for="date">
            Datum
          </label>
          <div className="input-group">
            <input type="date" className="form-control" id="date" name="date" />
          </div>
        </div>

        <div className="form-group col-12">
          <label className="text-dark" for="value">
            Summe
          </label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              id="value"
              name="value"
            />
          </div>
        </div>
      </Modal>

      <div
        className={`offcanvas offcanvas-bottom article-secondary-mobile ${
          showDividendEdit && "article-secondary-visible show"
        }`}
        tabIndex="-1"
        id="offcanvasBottom"
        aria-labelledby="offcanvasLabel"
      >
        <div className="offcanvas-header">
          <button
            type="button"
            className="btn-close-custom dismiss-active-or-focus"
          >
            <i
              className="icon-click icon-close bi bi-x-lg text-muted"
              onClick={() => setShowDividendEdit((prev) => !prev)}
            />
          </button>
        </div>
        <div className="offcanvas-body">
          <div className="article-secondary-info">
            {selectedRow?.company?.logo && (
              <Image
                source={selectedRow?.company?.logo}
                className="article-logo"
              />
            )}

            <h4 className="text-dark text-muted text-truncate">
              {selectedRow?.company?.name}
            </h4>

            <div
              id="data-description"
              className="text-muted text-left article-base-description-active"
            >
              <form id="updateTrade">
                <div className="form-group col-12">
                  <label className="text-dark" for="date">
                    Datum
                  </label>
                  <div className="input-group">
                    <input
                      type="date"
                      className="form-control"
                      id="date"
                      name="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group col-12">
                  <label className="text-dark" for="value">
                    Summe
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="value"
                      name="value"
                      value={dividendValue}
                      onChange={(e) => setDividendValue(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ marginLeft: "15px" }}>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    style={{ marginRight: "5px" }}
                    onClick={() => {
                      deleteDividend(selectedRow?.ticker, selectedRow?.date)
                        .then((result) => {
                          createNotification("success", result.message);
                          queryClient.invalidateQueries(["myTotalDividends"]);
                          queryClient.invalidateQueries(["mydividends"]);
                          setShowDividendEdit(false);
                        })
                        .catch((err) => {
                          createNotification("error", err.message);
                        });
                    }}
                  >
                    Löschen
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-search-custom dismiss-active-or-focus"
                    onClick={(e) => {
                      e.preventDefault(); //

                      if (date && dividendValue) {
                        updateDividend(
                          selectedRow?.ticker,
                          selectedRow?.date,
                          date,
                          dividendValue
                        )
                          .then((result) => {
                            createNotification("success", result.message);
                            queryClient.invalidateQueries(["myTotalDividends"]);
                            queryClient.invalidateQueries(["mydividends"]);
                            setShowDividendEdit(false);
                          })
                          .catch((err) => {
                            createNotification("error", err.message);
                          });
                      } else {
                        createNotification("error", "Bitte alle Daten angeben");
                      }
                    }}
                  >
                    Speichern
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Dividend;
